import type { ReactNode } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { COUNTRIES } from '@/lib/countries';
import type { CabinetProfile } from '../types';

function countryName(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code ?? '';
}

interface CabinetProfileFormProps {
  profile: CabinetProfile;
  /** Extra content rendered after the card (e.g. the employment record). */
  footer?: ReactNode;
}

/**
 * Read-only view of the member's own personal info, shown as label/value pairs
 * (matches the employment-record card). Every field is HR-managed — staff can
 * view but not edit; an Admin/Founder changes it via HRM.
 */
export function CabinetProfileForm({ profile, footer }: CabinetProfileFormProps) {
  const dob = profile.birth_date ? new Date(profile.birth_date).toLocaleDateString() : '—';
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal info</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="First name">{profile.first_name || '—'}</Field>
          <Field label="Last name">{profile.last_name || '—'}</Field>
          <Field label="Middle name">{profile.middle_name || '—'}</Field>
          <Field label="Work email">{profile.email || '—'}</Field>
          <Field label="Personal email">{profile.personal_email || '—'}</Field>
          <Field label="Phone">{profile.phone_number || '—'}</Field>
          <Field label="Date of birth">{dob}</Field>
          <Field label="City">{profile.city || '—'}</Field>
          <Field label="Country">{countryName(profile.country) || '—'}</Field>
          <Field label="Address" className="sm:col-span-3">
            <span className="whitespace-pre-line">{profile.address || '—'}</span>
          </Field>
        </CardContent>
      </Card>

      {footer}
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
